import React from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareRss } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, injectIntl } from 'react-intl';
import Cookies from 'js-cookie';
import { Container, Button, Row, Col } from 'reactstrap';

import env from 'Env';
import { getAuthorSlugByName } from 'Components/Blog/content/AuthorData';
import BlogAuthorBio from 'Components/Blog/content/BlogAuthorBio';
import BlogBreadcrumbs from 'Components/Utility/SEO/BlogBreadcrumbs';
import BlogPostStructuredData from 'Components/Blog/BlogPostStructuredData';
import BlogPostsList from 'Components/Blog/cards/BlogPostsList';
import BlogPreviewBanner from 'Components/Blog/BlogPreviewBanner';
import BlogProductsContainer from 'Components/Blog/BlogProductsContainer';
import BlogShare from 'Components/Blog/content/BlogShare';
import BlogSlice from 'Components/Blog/content/BlogSlice';
import BlogTags from 'Components/Blog/content/BlogTags';
import BlogText from 'Components/Blog/content/BlogText';
import FluidImg from 'Components/Utility/FluidImg';
import HomePageDealContainer from 'Components/HomePage/HomePageDealContainer';
import MegaHitCard from 'Components/Cards/MegaHitCard';
import Meta from 'Components/Utility/SEO/Meta';
import NoIndex from 'Components/Utility/SEO/NoIndex';
import EmailSignupPlacement from 'Components/Banner/EmailSignupPlacement/EmailSignupPlacement';

import 'Components/Blog/BlogPostPage.scss';
import 'Components/Blog/Shared-Blog.scss';

function BlogPostPage({
  post,
  relatedPosts,
  buyNowProduct,
  relatedProducts,
  viewMoreLink,
  viewMoreLabel,
  authenticated,
}) {
  if (isEmpty(post)) return false;
  const roolUrl = `${env.apiProtocol}://${env.apiUrl}`;
  const title = get(post, 'data.title[0].text', '');
  const author = get(post, 'data.author', 'Sam Jones');
  const guestAuthor = get(post, 'data.guest_author[0].text');
  const created = get(post, 'data.publication_datetime', '');
  const tags = post.tags;
  const introText = get(post, 'data.intro_text[0].text', '');
  const searchTerm = get(post, 'data.search_term[0].text', '');
  const bodyText = get(post, 'data.body_text', '');
  const bodyTextString = get(post, 'data.body_text[0].text', '');
  const slices = get(post, 'data.body', []);
  const slug = post.slugs[0];
  const category = get(post, 'data.category') || 'Games';

  const cover = get(post, 'data.cover');

  const srcSet = [];

  const smallUrl = get(cover, '224x126.url');
  const smallWidth = get(cover, '224x126.dimensions.width');
  const mediumUrl = get(cover, '400x225.url');
  const mediumWidth = get(cover, '400x225.dimensions.width');
  const largeUrl = get(cover, '752x423.url');
  const largeWidth = get(cover, '752x423.dimensions.width');

  const originalWidth = get(cover, 'dimensions.width');

  if (originalWidth) {
    srcSet.push(`${cover.url} ${originalWidth}w`);
  }

  if (largeUrl && largeWidth) {
    srcSet.push(`${largeUrl} ${largeWidth}w`);
  }
  if (mediumUrl && mediumWidth) {
    srcSet.push(`${mediumUrl} ${mediumWidth}w`);
  }
  if (smallUrl && smallWidth) {
    srcSet.push(`${smallUrl} ${smallWidth}w`);
  }

  let authorToDisplay = author;
  if (guestAuthor) authorToDisplay = guestAuthor;

  const authorSlug = getAuthorSlugByName(authorToDisplay);

  const isPreview = !!Cookies.get('io.prismic.preview');

  return (
    <main className="fanatical-blog BlogPostPage" key={slug}>
      {isPreview && <BlogPreviewBanner />}
      <BlogBreadcrumbs post={{ title, category, slug }} />
      <Meta
        title={`${title} | Fanatical Blog`}
        url={`blog/${post.uid}`}
        description={introText || bodyTextString}
        image={cover.url}
        pageType="ItemPage"
      />
      <BlogPostStructuredData post={post} buyNowProduct={buyNowProduct} />
      {window.locale !== 'en' &&
        <NoIndex />
      }
      <Container className="blog-post-page">
        <Row>
          <Col xs={12} lg={8}>
            <header className="blog-hero-container">
              <div className="responsive-image responsive-image--16by9">
                <img
                  srcSet={srcSet.join(', ')}
                  sizes="(min-width: 1520px) 872px, (min-width: 1200px) 740px, (min-width: 992px) 621px, (min-width: 768px) 704px, (min-width: 576px) 492px, 95vw"
                  src={cover.url}
                  alt={title}
                  className="img-fluid blog-hero-img img-force-full"
                />
              </div>
            </header>

            <article className="blog-article-container p-3">
              <Row className="mb-4">
                <Col xs={12} md={10} className="d-flex justify-content-center flex-column">
                  {title && <h1 className="blog-post-title">{title}</h1>}


                  {authorToDisplay &&
                    <div className="d-flex">
                      By &nbsp;
                      {authorSlug &&
                        <Link to={`/blog/authors/${authorSlug}`} className="blog-link">
                          {authorToDisplay}
                        </Link>
                      }
                      {!authorSlug && <span>{authorToDisplay}</span>}

                      <span className="d-md-none">
                        &nbsp;- {format(created, 'Do MMM YYYY')}
                      </span>

                      {category &&
                        <Link className={`blog-post-category-${category.toLowerCase()}`} to={`/blog/category/${category.toLowerCase()}`}>{category}</Link>
                      }

                    </div>
                  }


                </Col>
                <Col md={2} className="d-none d-md-block">
                  {created &&
                    <div className="blog-post-date">
                      <p className="date-number">{format(created, 'DD')}</p>
                      <p className="date-month">{format(created, 'MMM YY')}</p>
                    </div>
                  }
                </Col>
              </Row>

              {introText &&
                <div className="blog-page-intro mb-4">
                  <p>{introText}</p>
                </div>
              }

              <div className="d-md-none d-lg-none responsive-image responsive-image--16by9">
                {cover &&
                  <img src={cover.url} alt={title} loading="lazy" className="img-fluid mb-3" />
                }
              </div>

              {!isEmpty(buyNowProduct) &&
                <div className="blog-buy-now-container">
                  <MegaHitCard lightTheme hit={buyNowProduct} />
                </div>
              }

              {(bodyText && bodyText.length > 0) &&
                <div className="mb-5">
                  <BlogText elements={bodyText} />
                </div>
              }

              <div>
                {slices && slices.map(slice =>
                  <BlogSlice body={slice} key={`${slice.slice_type}+${Math.random()}`} />,
                )}
              </div>

              {authenticated &&
                <Link to={'/account/linked-accounts'}>
                  <div className="blog-wishlist-banner">
                    <FluidImg
                      alt={title}
                      folder="product"
                      src="33195985-cc6b-4fdd-bf04-82d3668046dd.jpeg"
                      className="img-fluid blog-card-img"
                    />
                  </div>
                </Link>
              }


              <hr className="blog-post-hr pt-2" />

              <BlogAuthorBio authorName={authorToDisplay} />

              {slug &&
                <div className="blog-post-share">
                  <hr className="blog-post-hr pt-2" />
                  <h2 className="h4">
                    <FormattedMessage id="blog-post-page.share" defaultMessage="Share this post" />
                  </h2>
                  <BlogShare
                    title={title}
                    url={`https://${env.appUrl}/en/blog/${post.uid}`}
                  />
                </div>
              }
            </article>

            <section>
              <h2 className="pb-3 h3">
                <FormattedMessage id="blog-post-page.may-like" defaultMessage="You may like " />
              </h2>
              <BlogProductsContainer
                hitCardLightTheme
                lgSize={4}
                existingHits={relatedProducts.slice(3, 6)}
              />
            </section>
          </Col>

          <Col xs={12} lg={4} className="BlogPostPage__aside">
            <EmailSignupPlacement
              appearsOn="blog_article"
              bannerSizes={{ lg: 'portrait', xl: 'portrait', xxl: 'portrait' }}
            />

            <HomePageDealContainer blog />

            <section>
              <div className="blog-products-container p-3 mb-0">
                <h2 className="pb-3 h3">
                  <FormattedMessage id="blog-post-page.related" defaultMessage="Related Products" />
                </h2>
                <BlogProductsContainer
                  hitCardLightTheme
                  searchTerm={searchTerm}
                  existingHits={relatedProducts.slice(0, 3)}
                  hitCardNameTag="h2"
                />
              </div>
              <Link to={viewMoreLink || `/search?search=${searchTerm}`} className="related-button">
                {viewMoreLabel || <FormattedMessage id="blog-post-page.view-more" defaultMessage="View more" />}
              </Link>
            </section>


            {!isEmpty(relatedPosts) &&
              <section>
                <div className="blog-page-related p-3">
                  <h2 className="pb-3 h3">
                    <FormattedMessage id="blog-post-page.related-posts" defaultMessage="Related Posts" />
                  </h2>
                  <div>
                    {relatedPosts.map(relatedPost =>
                      <BlogPostsList key={relatedPost.id} post={relatedPost} />,
                    )}
                  </div>
                </div>
              </section>
            }

            {!isEmpty(tags) &&
              <section className="blog-page-related p-3">
                <h2 className="pb-3 h3">Tags</h2>
                <div className="d-flex h-100 justify-content">
                  <BlogTags tags={tags} />
                </div>
              </section>
            }

            <Button
              color="primary"
              tag={'a'}
              href={`${roolUrl}/blog-feed`}
              size="lg"
              className="rss-btn"
            >
              <div className="rss-btn-icon">
                <FontAwesomeIcon icon={faSquareRss} color="white" />
              </div>
              <b>
                <FormattedMessage id="blog-post-page.subscribe-rss" defaultMessage="Subscribe to RSS feed" />
              </b>
            </Button>
          </Col>
        </Row>
      </Container>
    </main>
  );
}

BlogPostPage.propTypes = {
  post: PropTypes.shape({}),
  relatedPosts: PropTypes.arrayOf(PropTypes.object),
  relatedProducts: PropTypes.arrayOf(PropTypes.object),
  buyNowProduct: PropTypes.shape({}),
  viewMoreLink: PropTypes.string,
  viewMoreLabel: PropTypes.string,
  authenticated: PropTypes.bool,
};

BlogPostPage.defaultProps = {
  post: {},
  relatedPosts: [],
  relatedProducts: [],
  buyNowProduct: {},
  viewMoreLink: '',
  viewMoreLabel: '',
  authenticated: false,
};

export default injectIntl(BlogPostPage);
